import { FixedCostSettings, FixedCostTier } from '../api/FixedCostSettings/fixedCostSettings';

// Mirrors AvmFixedCostService::YearFixedCosts::RANGE_VALUES. The settings API returns the
// live table; this copy is only a fallback so the page can render before it loads.
export const SYSTEM_FIXED_COST_TIERS: FixedCostTier[] = [
  { index: 0, low: 0, high: 2, rate: 5, label: 'Up to 2' },
  { index: 1, low: 2, high: 10, rate: 12, label: '3-10' },
  { index: 2, low: 10, high: 25, rate: 25, label: '11-25' },
  { index: 3, low: 25, high: 35, rate: 30, label: '26-35' },
  { index: 4, low: 35, high: 40, rate: 35, label: '36-40' },
  { index: 5, low: 40, high: 45, rate: 37, label: '41-45' },
  { index: 6, low: 45, high: 85, rate: 50, label: '46-85' },
  { index: 7, low: 85, high: null, rate: 60, label: '86+' },
];

// Matches the database defaults on fixed_cost_settings, which reproduce the system calculation.
export const DEFAULT_FIXED_COST_SETTINGS: FixedCostSettings = {
  use_home_age: true,
  missing_age_behavior: 'zero',
  age_fallback_tier: 6,
  use_sqft: true,
  missing_sqft_behavior: 'zero',
  default_sqft: 2000,
  contingency_enabled: false,
  contingency_percent: 10,
  bounds_enabled: false,
  min_amount: 0,
  max_amount: null,
  custom_rates_enabled: false,
  custom_rates: [],
  apply_to_max_bid: true,
  apply_to_conditions: true,
};

export const MAX_RATE_PER_SQFT = 1000;
export const MAX_DEFAULT_SQFT = 50000;

export const tierIndexForAge = (age: number, tiers: FixedCostTier[] = SYSTEM_FIXED_COST_TIERS): number =>
  tiers.findIndex(t => t.high === null || age <= t.high);

export const effectiveRates = (settings: FixedCostSettings, tiers: FixedCostTier[] = SYSTEM_FIXED_COST_TIERS): number[] =>
  tiers.map((t, i) => (settings.custom_rates_enabled && settings.custom_rates?.[i] != null ? Number(settings.custom_rates[i]) : t.rate));

export type FixedCostBreakdown = {
  estimated_fixed_costs: number;
  home_age: number | null;
  home_age_source: 'property' | 'off' | 'fallback' | 'missing';
  tier_index: number | null;
  rate: number | null;
  sqft: number | null;
  sqft_source: 'property' | 'off' | 'fallback' | 'missing';
  base: number;
  contingency_amount: number;
  bound_applied: 'min' | 'max' | null;
  missing_data: string[];
};

// Same math as AvmFixedCostService::UserFixedCosts, used for the live preview on the settings page.
export const calculateFixedCosts = (
  settings: FixedCostSettings,
  property: { yearBuilt: number | null; sqft: number | null },
  currentYear: number = new Date().getFullYear(),
  tiers: FixedCostTier[] = SYSTEM_FIXED_COST_TIERS,
): FixedCostBreakdown => {
  const missing: string[] = [];
  const age = property.yearBuilt == null ? null : Math.abs(currentYear - property.yearBuilt);

  let tierIndex: number | null;
  let ageSource: FixedCostBreakdown['home_age_source'];
  if (!settings.use_home_age) {
    tierIndex = settings.age_fallback_tier;
    ageSource = 'off';
  } else if (age == null) {
    missing.push('year_built');
    tierIndex = settings.missing_age_behavior === 'fallback' ? settings.age_fallback_tier : null;
    ageSource = settings.missing_age_behavior === 'fallback' ? 'fallback' : 'missing';
  } else {
    tierIndex = tierIndexForAge(age, tiers);
    ageSource = 'property';
  }

  let sqft: number | null;
  let sqftSource: FixedCostBreakdown['sqft_source'];
  if (!settings.use_sqft) {
    sqft = settings.default_sqft;
    sqftSource = 'off';
  } else if (property.sqft != null && property.sqft > 0) {
    sqft = property.sqft;
    sqftSource = 'property';
  } else {
    missing.push('sqft');
    sqft = settings.missing_sqft_behavior === 'fallback' ? settings.default_sqft : null;
    sqftSource = settings.missing_sqft_behavior === 'fallback' ? 'fallback' : 'missing';
  }

  const empty = { home_age: age, home_age_source: ageSource, tier_index: tierIndex, sqft, sqft_source: sqftSource, missing_data: missing };
  if (tierIndex == null || sqft == null) {
    return { ...empty, estimated_fixed_costs: 0, rate: null, base: 0, contingency_amount: 0, bound_applied: null };
  }

  const rate = effectiveRates(settings, tiers)[tierIndex];
  const base = rate * sqft;
  const contingency = settings.contingency_enabled ? (base * settings.contingency_percent) / 100 : 0;
  let total = base + contingency;
  let bound: 'min' | 'max' | null = null;
  if (settings.bounds_enabled) {
    if (total < settings.min_amount) {
      total = settings.min_amount;
      bound = 'min';
    } else if (settings.max_amount != null && total > settings.max_amount) {
      total = settings.max_amount;
      bound = 'max';
    }
  }

  return { ...empty, estimated_fixed_costs: total, rate, base, contingency_amount: contingency, bound_applied: bound };
};

// Plain-language problems with the form, keyed by field; empty when it can be saved.
export const validateFixedCostSettings = (s: FixedCostSettings, tierCount = SYSTEM_FIXED_COST_TIERS.length): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!Number.isInteger(s.default_sqft) || s.default_sqft <= 0 || s.default_sqft > MAX_DEFAULT_SQFT) {
    errors.default_sqft = `Enter a whole number of square feet from 1 to ${MAX_DEFAULT_SQFT.toLocaleString()}.`;
  }
  if (!(s.contingency_percent >= 0 && s.contingency_percent <= 100)) {
    errors.contingency_percent = 'Enter a percent from 0 to 100.';
  }
  if (!(s.min_amount >= 0)) {
    errors.min_amount = 'Enter a minimum of $0 or more.';
  }
  if (s.max_amount != null && !(s.max_amount > 0 && s.max_amount >= s.min_amount)) {
    errors.max_amount = 'The maximum must be more than $0 and at least the minimum.';
  }
  if (s.custom_rates.length > 0) {
    const ok = s.custom_rates.length === tierCount && s.custom_rates.every(r => Number.isFinite(r) && r >= 0 && r <= MAX_RATE_PER_SQFT);
    if (!ok) errors.custom_rates = `Each rate must be from $0 to $${MAX_RATE_PER_SQFT} per sqft.`;
  }
  return errors;
};
